import { ref, readonly, watch } from 'vue';
import { useConvexQuery, useConvexClient } from 'convex-vue';
import { useI18n } from 'vue-i18n';
import { api } from '../../convex/_generated/api';
import type { SupportedLocale } from '../plugins/i18n';

export interface UserSettings {
  prefillNameOnIdeas: boolean;
  locale: SupportedLocale | null;
  hideWelcomeModal: boolean;
}

// Browser language detection (copied from i18n plugin since it's not exported)
function detectBrowserLanguage(): SupportedLocale {
  const browserLang = navigator.language?.toLowerCase().split('-')[0];
  return browserLang === 'de' ? 'de' : 'en';
}

// Global singleton state
const settings = ref<UserSettings | null>(null);
const isPending = ref(true);

export function useUserSettings() {
  const client = useConvexClient();
  const { locale } = useI18n();

  // Query user settings from Convex
  const query = useConvexQuery(api.userSettings.get, () => ({}));

  // Sync global state with query results
  watch(
    () => query.data.value,
    newData => {
      if (newData) {
        settings.value = {
          prefillNameOnIdeas: newData.prefillNameOnIdeas,
          locale: newData.locale ?? null,
          hideWelcomeModal: newData.hideWelcomeModal ?? false,
        };
      } else {
        settings.value = null;
      }
      isPending.value = query.isPending.value;

      // Update i18n locale when settings load or change
      if (newData?.locale) {
        locale.value = newData.locale;
      } else if (newData?.locale === null || newData?.locale === undefined) {
        // Use browser default when locale is null or undefined
        locale.value = detectBrowserLanguage();
      }
    },
    { immediate: true }
  );

  // Update function for mutating settings
  const update = async (args: {
    prefillNameOnIdeas?: boolean;
    locale?: SupportedLocale | null;
    hideWelcomeModal?: boolean;
  }) => {
    await client.mutation(api.userSettings.update, args);
  };

  return {
    data: readonly(settings),
    isPending: readonly(isPending),
    update,
  };
}
